import { State } from "redux/store";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import {
  EnrichingDS,
  EnrichmentModel,
  EnrichmentModelForPreview,
  EnrichmentModelOption,
  ModelSelectionData,
  EnrichmentStatus,
  ModelTypes,
  SelectionAlertModel,
  DatasetStatus,
} from "types";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { getDataset } from "redux/SingleDataset/selectors";
import { usePromptSupportOptions } from "hooks/useFeatureFlagHooks";
import AbortModal from "./AbortModal";
import vlIcon from "assets/img/vllogo.svg";
import PreviewSection from "./PreviewsSection";
import gptIcon from "assets/img/gpt_symbol.svg";
import ModelOptionsModal from "./ModelOptionsModal";
import ModelProgressModal from "./ModelProgressModal";
import nvidiaIcon from "assets/img/nvidia_symbol.svg";
import SelectionAlertModal from "./SelectionAlertModal";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";
import ContainerHeader from "views/components/ContainerHeader";
import {
  getEnrichedTargetDatasetId,
  getEnrichmentModelList,
  getModelSelectionData,
  getEnrichmentStatus,
  getPreviewModels,
  getSelectedModelId,
  getShowEnrichmentProgress,
  getUserSelectedPreviewModels,
  getPreviousDatasetEnrichmentModels,
} from "redux/Enrichment/selectors";
import { getEnrichingDataset } from "redux/Datasets/selectors";
import {
  fetchEnrichmentIntialData,
  fetchPreviewForSingleModel,
  setEnrichmentPreviewData,
  setUserSelectedPreviewModels,
  startEnrichment,
  setShowEnrichmentProgress,
  setSelectedModelId,
  resetStore,
  abortEnrichment,
  setEnrichmentProgress,
  setModelSelectionData,
  setPreviousDatasetEnrichmentModels,
  updateModelPrompt,
} from "redux/Enrichment/actions";
import { fetchDataset } from "redux/SingleDataset/actions";
import { setEnrichingDataset } from "redux/Datasets/actions";
import { createContent } from "./helpers";
import {
  AMP_ENRICHMENT_PROPERTY__MODEL_NAME,
  AMP_ENRICHMENT_PROPERTY__MODEL_NAMES,
} from "helpers/constants/amplitudeProperties";
import {
  AMP_ENRICHMENT_EVENT__COMING_SOON_CLICKED,
  AMP_ENRICHMENT_EVENT__MODEL_ADDED,
  AMP_ENRICHMENT_EVENT__MODEL_REMOVED,
  AMP_ENRICHMENT_EVENT__RUN_ABORTED,
} from "helpers/constants/amplitudeEvents";
import styles from "./style.module.scss";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import UnavailableModal from "views/components/UnavillableModal/UnavailableModal";
import { useUserConfig } from "../../../hooks/useUserConfig";

const EnrichmentPage = ({
  previewModels,
  selectedModelId,
  enrichmentModelList,
  showEnrichmentProgress,
  enrichmentTargetDatasetId,
  enrichmentStatus,
  modelSelectionData,
  enrichingDataset,
  alreadyEnrichedModels,
  userSelectedPreviewModels,
}: EnrichmentPageProps) => {
  const promptSupportEnabled = usePromptSupportOptions().enabled;

  const initalShowModelOptions = previewModels.length < 1;
  const [modelList, setModelList] =
    useState<EnrichmentModelOption[]>(enrichmentModelList);
  const [showAbortModal, setShowAbortModal] = useState(false);
  const [showModelOptions, setShowModelOptions] = useState(
    initalShowModelOptions,
  );
  const [modelToBeOverWritten, setModelToBeOverWritten] =
    useState<EnrichmentModelForPreview | null>(null);
  const [selectionAlertModel, setSelectionAlertModel] =
    useState<SelectionAlertModel | null>(null);
  const [selectedModel, setSelectedModel] =
    useState<EnrichmentModelOption | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [localModels, setLocalModels] = useState<EnrichmentModel[]>([]);

  const { enrichDatasetId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const replacedModels = alreadyEnrichedModels.filter(
    (model) => model.replaced,
  );
  const { refetch: refetchUserConfig } = useUserConfig();

  useEffect(() => {
    if (showEnrichmentProgress) {
      setShowModelOptions(false);
    }
    dispatch(fetchDataset(enrichDatasetId) as any);
    refetchUserConfig();
    //eslint-disable-next-line
  }, [enrichDatasetId, showEnrichmentProgress]);

  useEffect(() => {
    return () => {
      dispatch(resetStore());
    };
    //eslint-disable-next-line
  }, []);

  const fetchInitialPreviews = async () => {
    if (!localModels.length || !firstLoad || !previewModels.length) {
      return;
    }

    if (modelSelectionData) {
      let selectedModel =
        modelSelectionData[enrichDatasetId!].selectedModel || null;
      if (selectedModel && Object.keys(selectedModel).length) {
        setShowModelOptions(false);
        dispatch(setSelectedModelId(selectedModel.id));
        dispatch(
          fetchPreviewForSingleModel(selectedModel, enrichDatasetId!) as any,
        );
      }
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchInitialPreviews();
    //eslint-disable-next-line
  }, [previewModels, firstLoad]);

  const handleShowModelOptions = () => {
    setShowModelOptions(true);
  };

  const showProgressModal = (value: boolean) =>
    dispatch(setShowEnrichmentProgress(value));

  const removeModelFromSelectionData = (
    modelToRemove: EnrichmentModelForPreview,
  ) => {
    let data = modelSelectionData?.[enrichDatasetId!] || null;
    if (data) {
      let { models = [], selectedModel = null } = { ...data };
      models = models.filter((prev) => prev.id !== modelToRemove.id);

      selectedModel =
        selectedModel?.id === modelToRemove.id ? null : selectedModel;

      let newSelectionData = {
        ...modelSelectionData,
        [enrichDatasetId!]: { models, selectedModel },
      };

      dispatch(setModelSelectionData(newSelectionData));
    }
  };

  const removeModelFromUserPreviewModels = (modelToRemove: EnrichmentModel) => {
    const updatedModels = userSelectedPreviewModels.filter(
      (model) => model.id !== modelToRemove.id,
    );
    dispatch(setUserSelectedPreviewModels(updatedModels));
  };

  const removeReplacedFlagFromPreviousModels = (
    replacedModel: EnrichmentModel,
  ) => {
    const updatedPreviousModels = alreadyEnrichedModels.map((model) => {
      const replaced =
        replacedModel.id === model.id ? false : model.replaced || false;

      return { ...model, replaced };
    });

    dispatch(setPreviousDatasetEnrichmentModels(updatedPreviousModels));
  };

  const handleModelRemoval = (id: string) => {
    const previewModel = previewModels.find((model) => model.id === id);

    if (!previewModel || previewModel.deletable.value === false) {
      return;
    }

    if (selectedModelId === id || previewModels.length < 2) {
      dispatch(setEnrichmentPreviewData([]));
      dispatch(setSelectedModelId(null));
      if (selectedModelId === id) {
        setSelectedModel(null);
      }
    }
    if (previewModels.length === 1) {
      setShowModelOptions(true);
    }
    if (previewModels.length > 1 && selectedModelId === id) {
      const newModels = previewModels.filter((model) => model.id !== id);
      handleModelSelection(newModels[0].id);
    }

    const newPreviewModels = userSelectedPreviewModels.filter(
      (model) => model.id !== id,
    );

    dispatch(setUserSelectedPreviewModels(newPreviewModels));
    removeModelFromSelectionData(previewModel);

    //check if replaced models need to be reset
    if (replacedModels.length) {
      const replacedModel = replacedModels.find(
        (model) => model.type === previewModel.type,
      );

      if (replacedModel) {
        removeReplacedFlagFromPreviousModels(replacedModel);
      }
    }

    if (!alreadyEnrichedModels.length && newPreviewModels.length === 0) {
      setShowModelOptions(true);
    }

    //track model removal
    const modelName = modelList.find((item) => item.id === id)?.name;

    amplitudeTrack(AMP_ENRICHMENT_EVENT__MODEL_REMOVED, {
      [AMP_ENRICHMENT_PROPERTY__MODEL_NAME]: modelName,
    });
  };

  const handleCloseModels = () => {
    setShowModelOptions(false);
    setSelectedModel(null);
  };

  const setEnrichmentLocalData = (
    datasetId: string,
    selectedModel: EnrichmentModel,
  ) => {
    const enrichData: any = { ...modelSelectionData };

    let updatedEnrichmentData;
    if (enrichData) {
      let datasetEnrichData: any = enrichData?.[datasetId] || {};
      let updatedData;
      if (datasetEnrichData?.models?.length > 0) {
        updatedData = {
          models: [...datasetEnrichData.models, selectedModel],
          selectedModel,
        };
      } else {
        updatedData = { models: [selectedModel], selectedModel };
      }
      updatedEnrichmentData = { ...enrichData, [datasetId!]: updatedData };
    } else {
      updatedEnrichmentData = {
        [datasetId]: { models: [selectedModel], selectedModel: selectedModel },
      };
    }
    dispatch(setModelSelectionData(updatedEnrichmentData));
  };

  const handleCreateModelPreviews = (model: EnrichmentModel) => {
    if (model.selectable.value) {
      dispatch(setSelectedModelId(model.id));
      handleCloseModels();
      dispatch(
        setUserSelectedPreviewModels([model, ...userSelectedPreviewModels]),
      );
      amplitudeTrack(AMP_ENRICHMENT_EVENT__MODEL_ADDED, {
        [AMP_ENRICHMENT_PROPERTY__MODEL_NAME]: model.name,
      });
      const promptable = model.vqa_prompt && promptSupportEnabled;
      const missingPrompt = model.metadata?.prompt?.length;
      if (promptable && !missingPrompt) {
        dispatch(updateModelPrompt({ modelId: model.id, prompt: "" }));
      }
      dispatch(fetchPreviewForSingleModel(model, enrichDatasetId!) as any);
      setEnrichmentLocalData(enrichDatasetId!, model);
    }
  };

  const handleModelSelection = (modelId: string, newPrompt?: boolean) => {
    if (!newPrompt && selectedModelId === modelId) {
      return;
    } else {
      dispatch(setSelectedModelId(modelId));

      const selectedModel = previewModels.find((model) => model.id === modelId);
      let enrichData = { ...modelSelectionData };
      let updatedEnrichmentData;
      if (!enrichData) {
        return;
      }
      let localData: any;
      if (enrichDatasetId && enrichData?.[enrichDatasetId]) {
        localData = { ...enrichData[enrichDatasetId] };
        localData.selectedModel = selectedModel;
        updatedEnrichmentData = {
          ...enrichData,
          [enrichDatasetId]: { ...localData, selectedModel: selectedModel },
        };
      } else {
        localData = { models: [selectedModel], selectedModel: selectedModel };

        updatedEnrichmentData = {
          ...enrichData,
          [enrichDatasetId!]: { ...localData, selectedModel: selectedModel },
        };
      }

      dispatch(setModelSelectionData(updatedEnrichmentData));
      dispatch(
        fetchPreviewForSingleModel(selectedModel!, enrichDatasetId!) as any,
      );
    }
  };

  const handleStartEnrichment = () => {
    dispatch(setEnrichmentProgress(0));
    dispatch(startEnrichment(enrichDatasetId!) as any);
  };

  const handleCancelAbort = () => {
    setShowAbortModal(false);
    showProgressModal(true);
  };

  const handleConfirmAbort = async () => {
    await dispatch(abortEnrichment(enrichDatasetId!) as any);
    dispatch(fetchEnrichmentIntialData(enrichDatasetId!) as any);
    if (enrichingDataset) {
      dispatch(setEnrichingDataset(null));
    }
    setShowAbortModal(false);
    showProgressModal(false);

    //track enrichment abortion
    const modelNames = previewModels.map((model) => model.name);
    const modelNameKey =
      modelNames.length > 1
        ? AMP_ENRICHMENT_PROPERTY__MODEL_NAMES
        : AMP_ENRICHMENT_PROPERTY__MODEL_NAME;

    amplitudeTrack(AMP_ENRICHMENT_EVENT__RUN_ABORTED, {
      [modelNameKey]: modelNames.join(", "),
    });
  };

  const handleAbortProgress = () => {
    setShowAbortModal(true);
    showProgressModal(false);
  };

  const handleExploreDataset = () => {
    setShowAbortModal(false);
    showProgressModal(false);
    const datasetId =
      enrichmentStatus && enrichmentStatus === EnrichmentStatus.COMPLETED
        ? enrichmentTargetDatasetId
        : enrichDatasetId;
    navigate(`/dataset/${datasetId}/data`);
  };

  const handleNewEnrichmentClick = () => {
    setShowAbortModal(false);
    showProgressModal(false);
    navigate(`/dataset/${enrichmentTargetDatasetId!}/enrich`);
    dispatch(fetchEnrichmentIntialData(enrichmentTargetDatasetId!) as any);
  };

  const handleRetryEnrichment = () => {
    setShowAbortModal(false);
    showProgressModal(false);
    dispatch(fetchEnrichmentIntialData(enrichDatasetId!) as any);
    dispatch(setUserSelectedPreviewModels([]));
    setShowModelOptions(true);
  };

  const handleAcceptOverrideConfirmation = () => {
    if (modelToBeOverWritten) {
      //remove model from  selection data
      removeModelFromSelectionData(modelToBeOverWritten);
      removeModelFromUserPreviewModels(modelToBeOverWritten);

      //remove preview data if model was selected
      if (selectedModelId === modelToBeOverWritten.id) {
        dispatch(setSelectedModelId(null));
        dispatch(setEnrichmentPreviewData([]));
      }

      //reset the replaced flag
      if (modelToBeOverWritten.replaced) {
        removeReplacedFlagFromPreviousModels(modelToBeOverWritten);
      } else {
        //mark hidden model with replaced key
        const updatedPreviousModels = alreadyEnrichedModels.map((model) => {
          return {
            ...model,
            replaced: model.type === selectionAlertModel?.type,
          };
        });
        dispatch(setPreviousDatasetEnrichmentModels(updatedPreviousModels));
      }
    }

    setSelectedModel(selectionAlertModel);

    setSelectionAlertModel(null);
    setModelToBeOverWritten(null);
  };

  const handleDeclineConfirmation = () => {
    setSelectionAlertModel(null);
    setModelToBeOverWritten(null);
  };

  const handleReplacedModalSelection = (model: EnrichmentModelOption) => {
    //clear out the replaced model if the user selects the replacing model again or selects the replaced model
    const clickedReplacedModel = !!replacedModels.find(
      (item) => item.id === model.id,
    );
    const clickedReplacingModel =
      replacedModels.every((item) => item.id !== model.id) &&
      replacedModels.some((item) => item.type === model.type);

    if (clickedReplacedModel || clickedReplacingModel) {
      const replacedModel = replacedModels.find(
        (item) => item.id === model.id || item.type === model.type,
      );

      const replacingModel = userSelectedPreviewModels.find(
        (item) => item.type === model?.type,
      );

      removeReplacedFlagFromPreviousModels(replacedModel!);

      removeModelFromUserPreviewModels(replacingModel!);
      removeModelFromSelectionData(replacingModel! as any);

      if (selectedModelId === model.id) {
        dispatch(setSelectedModelId(null));
      }

      setSelectedModel(null);
      return true;
    }
    return false;
  };

  const handleSameTypeModelSelection = (model: EnrichmentModelOption) => {
    const enrichedModelWithSameType = alreadyEnrichedModels.find(
      (item) => item.type === model.type,
    );

    const userSelectedModelWithSameType = userSelectedPreviewModels.find(
      (item) => item.type === model.type,
    );

    if (enrichedModelWithSameType || userSelectedModelWithSameType) {
      const sameTypeModel =
        enrichedModelWithSameType || userSelectedModelWithSameType;
      setModelToBeOverWritten(sameTypeModel as any);
      setSelectionAlertModel({ ...model, selectionType: "override" });
      return true;
    }
    return false;
  };

  const handleSemanticObjectSelection = (model: EnrichmentModelOption) => {
    if (model.type !== ModelTypes.MULTIMODEL_OBJECT_ENCODING) {
      return false;
    }

    if (selectedModel?.id === model.id) {
      setSelectedModel(null);
    } else {
      setSelectionAlertModel({ ...model, selectionType: "alert" });
    }
    return true;
  };

  const handleOptionSelection = (
    model: EnrichmentModelOption,
    isActive: boolean,
  ) => {
    // track
    if (!isActive) {
      amplitudeTrack(AMP_ENRICHMENT_EVENT__COMING_SOON_CLICKED, {
        AMP_ENRICHMENT_PROPERTY__MODEL_NAME: model.name,
      });
      return;
    }

    // 1. Check if model is selectable
    if (!model.selectable.value) return;

    // 2. Check if the replaced model was selected and handle it
    const wasReplacedModel = handleReplacedModalSelection(model);
    if (wasReplacedModel) return;

    // 3. Check if the current selection has same type as a selected model
    const hadSameType = handleSameTypeModelSelection(model);
    if (hadSameType) return;

    // 4. Check if the current selection has type - Semantic object
    const wasSemanticObjectModel = handleSemanticObjectSelection(model);
    if (wasSemanticObjectModel) return;

    // 5. Toggle the selected state of selected model
    setSelectedModel((prev) => (prev?.id === model.id ? null : model));
  };

  const renderSelectionAlertModal = () => {
    if (!selectionAlertModel) return <noscript />;

    const isAlert = selectionAlertModel.selectionType === "alert";
    const titleText = isAlert ? "Just a quick heads-up" : "Override Operation";

    const iconClassnames = classNames(styles.titleIcon, {
      [styles.warningIcon]: isAlert,
    });

    // Title Icon
    const icon = (
      <div className={iconClassnames}>
        <DangerCircleIcon variant="filled" />
      </div>
    );

    const title = <span className={styles.title}>{titleText}</span>;
    const content = createContent(selectionAlertModel);

    return (
      <SelectionAlertModal
        icon={icon}
        title={title}
        content={content}
        onDecline={handleDeclineConfirmation}
        onAccept={handleAcceptOverrideConfirmation}
      />
    );
  };

  useEffect(() => {
    dispatch(fetchEnrichmentIntialData(enrichDatasetId!) as any);
  }, [dispatch, enrichDatasetId]);

  useEffect(() => {
    setModelList(enrichmentModelList);
  }, [enrichmentModelList]);

  useEffect(() => {
    let enrichData = { ...modelSelectionData };
    let models = enrichData?.[enrichDatasetId!]?.models || [];
    setLocalModels(models);
    //eslint-disable-next-line
  }, []);

  const dataset = useSelector(getDataset);

  const getInnerBody = () => {
    if (!dataset) {
      return <VLColorfulLoader />;
    }

    const isEnrichmentAllowed = [
      DatasetStatus.READ_ONLY,
      DatasetStatus.READY,
    ].includes(dataset.status);

    if (!isEnrichmentAllowed) {
      const errorMessage = (
        <div>Enrichment is disabled for {dataset.status} datasets.</div>
      );

      return (
        <UnavailableModal
          actionText={"Go to dataset page"}
          onClose={() => navigate(`/dataset/${dataset.id}/data?page=1`)}
          description={errorMessage}
        />
      );
    }

    return (
      <div className={styles.innerBody}>
        <PreviewSection
          previewModels={previewModels}
          selectedModelId={selectedModelId}
          onRemoveModel={handleModelRemoval}
          onSelectModel={handleModelSelection}
          onShowModelOptions={handleShowModelOptions}
          onStartEnrichment={handleStartEnrichment}
        />
        {showModelOptions && modelList.length > 0 && (
          <ModelOptionsModal
            modelOptions={modelList}
            selectedModel={selectedModel}
            onCloseModelOptions={handleCloseModels}
            onOptionSelection={handleOptionSelection}
            onCreatePreviews={handleCreateModelPreviews}
          />
        )}

        {showAbortModal && (
          <AbortModal
            onCancelClick={handleCancelAbort}
            onConfirmClick={handleConfirmAbort}
          />
        )}

        {renderSelectionAlertModal()}

        {showEnrichmentProgress && (
          <ModelProgressModal
            onAbortClick={handleAbortProgress}
            onExploreClick={handleExploreDataset}
            onRetryClick={handleRetryEnrichment}
            onNewEnrichmentClick={handleNewEnrichmentClick}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Dataset Enrichment</title>
        <link rel="preload" href={vlIcon} as="image" type="image/svg+xml" />
        <link rel="preload" href={gptIcon} as="image" type="image/svg+xml" />
        <link rel="preload" href={nvidiaIcon} as="image" type="image/svg+xml" />
      </Helmet>
      <div className={styles.headerContainer}>
        <ContainerHeader />
      </div>
      {getInnerBody()}
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    previewModels: getPreviewModels(state),
    selectedModelId: getSelectedModelId(state),
    enrichmentStatus: getEnrichmentStatus(state),
    enrichingDataset: getEnrichingDataset(state),
    modelSelectionData: getModelSelectionData(state),
    enrichmentModelList: getEnrichmentModelList(state),
    showEnrichmentProgress: getShowEnrichmentProgress(state),
    enrichmentTargetDatasetId: getEnrichedTargetDatasetId(state),
    userSelectedPreviewModels: getUserSelectedPreviewModels(state),
    alreadyEnrichedModels: getPreviousDatasetEnrichmentModels(state),
  };
};

export default connect(mapStateToProps)(EnrichmentPage);

interface EnrichmentPageProps {
  previewModels: EnrichmentModelForPreview[];
  userSelectedPreviewModels: EnrichmentModel[];
  showEnrichmentProgress: boolean;
  selectedModelId: string | null;
  enrichmentModelList: EnrichmentModelOption[];
  enrichmentTargetDatasetId: string | null;
  enrichmentStatus: EnrichmentStatus | null;
  modelSelectionData: ModelSelectionData | null;
  enrichingDataset: null | EnrichingDS;
  alreadyEnrichedModels: EnrichmentModel[];
}
